import { Stack, Typography, Box, useTheme } from '@mui/material';
import { AutoIsoPlayer } from '@/features/autoIso/types/AutoIsoModel';
import { TeamModel } from '../types/TeamModel';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { PlayerItem } from './PlayerItem';
import { memo, useMemo } from 'react';
import SimpleBar from 'simplebar-react';

// NBA rosters are detected from these position codes (NHL codes never include them),
// so the hockey grouping below stays untouched. Guard/Forward/Center mirror the
// league.playerCategories config seeded for the NBA league.
const NBA_POSITION_GROUPS: { label: string; positions: string[] }[] = [
  { label: 'Guard', positions: ['PG', 'SG', 'G'] },
  { label: 'Forward', positions: ['SF', 'PF', 'F'] },
  { label: 'Center', positions: ['C'] },
];
const NBA_POSITION_CODES = new Set(['PG', 'SG', 'SF', 'PF']);

type Props = {
  team: TeamModel;
  players: AutoIsoPlayer[];
  onPlayerClick?: (player: AutoIsoPlayer) => void;
  sx?: object;
  index?: number;
  singleCurrentlyTrackedObjectId?: number | null;
  showGroupTitles?: boolean;
  disableNameEllipsis?: boolean;
  trackedItemSx?: object;
};

export const TeamPlayersList = memo(
  ({
    team,
    players,
    onPlayerClick,
    sx,
    index,
    singleCurrentlyTrackedObjectId,
    showGroupTitles = true,
    disableNameEllipsis,
    trackedItemSx,
  }: Props) => {
    const teamLogoPath = useMemo(() => getTeamLogoUrl(team), [team]);
    const teamColor = useMemo(() => team.teamColors?.[0]?.hex_color || '#000000', [team]);

    // Group players by position category
    const groupedPlayers = useMemo(() => {
      const getPositionCategory = (
        position: string,
      ): 'forwards' | 'defensemen' | 'goaltenders' | 'other' => {
        const pos = position.toUpperCase();
        // Forwards: C, LW, RW, F
        if (pos.includes('C') || pos.includes('W') || pos === 'F') return 'forwards';
        // Defensemen: D, LD, RD
        if (pos.includes('D') && !pos.includes('G')) return 'defensemen';
        // Goaltenders: G
        if (pos.includes('G')) return 'goaltenders';
        return 'other';
      };

      const groups = {
        forwards: [] as AutoIsoPlayer[],
        defensemen: [] as AutoIsoPlayer[],
        goaltenders: [] as AutoIsoPlayer[],
        other: [] as AutoIsoPlayer[],
      };

      players.forEach((player) => {
        const category = getPositionCategory(player.playerPosition);
        groups[category].push(player);
      });

      return groups;
    }, [players]);

    // Detect a basketball roster from its position codes. NHL positions never contain
    // these, so the hockey grouping/labels above are never affected.
    const isBasketball = useMemo(
      () => players.some((p) => NBA_POSITION_CODES.has((p.playerPosition || '').toUpperCase())),
      [players],
    );

    // NBA grouping: Guard / Forward / Center (+ Other). Prefer the backend-computed
    // playerCategory (from league.playerCategories) and fall back to the raw position
    // code so grouping still works before/without the config being seeded.
    const nbaGroups = useMemo(() => {
      const labels = NBA_POSITION_GROUPS.map((g) => g.label);
      const labelForPosition = (position: string) => {
        const pos = (position || '').toUpperCase();
        return NBA_POSITION_GROUPS.find((g) => g.positions.includes(pos))?.label ?? 'Other';
      };

      const buckets: Record<string, AutoIsoPlayer[]> = {};
      players.forEach((player) => {
        const label =
          player.playerCategory && labels.includes(player.playerCategory)
            ? player.playerCategory
            : labelForPosition(player.playerPosition);
        (buckets[label] ||= []).push(player);
      });

      return [...labels, 'Other']
        .filter((label) => buckets[label]?.length)
        .map((label) => ({ label, players: buckets[label] }));
    }, [players]);

    // Memoize SimpleBar style to prevent re-creation
    const simpleBarStyle = useMemo(() => ({ height: '100%', flex: 1 }), []);

    // Memoize Stack sx to prevent re-creation
    const stackSx = useMemo(() => ({ width: '100%', py: 0.5, pl: index === 0 ? 0 : 0.5 }), [index]);

    const theme = useTheme();

    // Renders a single labeled position group. Used by the NBA branch; the NHL
    // branch keeps its original hardcoded sections below.
    const renderGroup = (label: string, groupPlayers: AutoIsoPlayer[]) => (
      <Box key={label}>
        {showGroupTitles && (
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              fontHeight: '16px',
              color: theme.palette.cx.text.secondary,
              letterSpacing: '0px',
              mb: 0.5,
              mt: 0.5,
            }}
          >
            {label}
          </Typography>
        )}
        {groupPlayers.map((player) => (
          <PlayerItem
            key={player.id}
            player={player}
            teamLogoPath={teamLogoPath}
            teamColor={teamColor}
            onPlayerClick={onPlayerClick}
            isTrackedObject={singleCurrentlyTrackedObjectId === player.id}
            disableNameEllipsis={disableNameEllipsis}
            trackedItemSx={trackedItemSx}
          />
        ))}
      </Box>
    );

    return (
      <SimpleBar autoHide={false} style={simpleBarStyle}>
        <Stack spacing={0.3} sx={stackSx}>
          {isBasketball ? (
            nbaGroups.map((group) => renderGroup(group.label, group.players))
          ) : (
            <>
          {/* Forwards */}
          {groupedPlayers.forwards.length > 0 && (
            <>
              {showGroupTitles && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    fontHeight: '16px',
                    color: theme.palette.cx.text.secondary,
                    letterSpacing: '0px',
                    mb: 0.5,
                    mt: 0.5,
                  }}
                >
                  Forwards
                </Typography>
              )}
              {groupedPlayers.forwards.map((player) => (
                <PlayerItem
                  key={player.id}
                  player={player}
                  teamLogoPath={teamLogoPath}
                  teamColor={teamColor}
                  onPlayerClick={onPlayerClick}
                  isTrackedObject={singleCurrentlyTrackedObjectId === player.id}
                  disableNameEllipsis={disableNameEllipsis}
                  trackedItemSx={trackedItemSx}
                />
              ))}
            </>
          )}

          {/* Defensemen */}
          {groupedPlayers.defensemen.length > 0 && (
            <>
              {showGroupTitles && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    fontHeight: '16px',
                    color: theme.palette.cx.text.secondary,
                    letterSpacing: '0px',
                    mb: 0.5,
                    mt: 0.5,
                  }}
                >
                  Defensemen
                </Typography>
              )}
              {groupedPlayers.defensemen.map((player) => (
                <PlayerItem
                  key={player.id}
                  player={player}
                  teamLogoPath={teamLogoPath}
                  teamColor={teamColor}
                  onPlayerClick={onPlayerClick}
                  isTrackedObject={singleCurrentlyTrackedObjectId === player.id}
                  disableNameEllipsis={disableNameEllipsis}
                  trackedItemSx={trackedItemSx}
                />
              ))}
            </>
          )}

          {/* Goaltenders */}
          {groupedPlayers.goaltenders.length > 0 && (
            <>
              {showGroupTitles && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    fontHeight: '16px',
                    color: theme.palette.cx.text.secondary,
                    letterSpacing: '0px',
                    mb: 0.5,
                    mt: 0.5,
                  }}
                >
                  Goaltenders
                </Typography>
              )}
              {groupedPlayers.goaltenders.map((player) => (
                <PlayerItem
                  key={player.id}
                  player={player}
                  teamLogoPath={teamLogoPath}
                  teamColor={teamColor}
                  onPlayerClick={onPlayerClick}
                  isTrackedObject={singleCurrentlyTrackedObjectId === player.id}
                  disableNameEllipsis={disableNameEllipsis}
                  trackedItemSx={trackedItemSx}
                />
              ))}
            </>
          )}

          {/* Other positions (if any) */}
          {groupedPlayers.other.length > 0 && (
            <Box>
              {showGroupTitles && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    fontHeight: '16px',
                    color: theme.palette.cx.text.secondary,
                    letterSpacing: '0px',
                    mb: 0.5,
                    mt: 0.5,
                  }}
                >
                  Other
                </Typography>
              )}
              {groupedPlayers.other.map((player) => (
                <PlayerItem
                  key={player.id}
                  player={player}
                  teamLogoPath={teamLogoPath}
                  teamColor={teamColor}
                  onPlayerClick={onPlayerClick}
                  isTrackedObject={singleCurrentlyTrackedObjectId === player.id}
                  disableNameEllipsis={disableNameEllipsis}
                  trackedItemSx={trackedItemSx}
                />
              ))}
            </Box>
          )}
            </>
          )}
        </Stack>
      </SimpleBar>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if props actually changed
    // if (prevProps.players.length !== nextProps.players.length) return false;
    if (prevProps.team.id !== nextProps.team.id) return false;
    if (prevProps.onPlayerClick !== nextProps.onPlayerClick) return false;
    if (prevProps.singleCurrentlyTrackedObjectId !== nextProps.singleCurrentlyTrackedObjectId)
      return false;
    if (prevProps.index !== nextProps.index) return false;
    if (prevProps.showGroupTitles !== nextProps.showGroupTitles) return false;

    // Deep comparison of player IDs
    if (prevProps.players.length !== nextProps.players.length) return false;
    for (let i = 0; i < prevProps.players.length; i++) {
      if (prevProps.players[i].id !== nextProps.players[i].id) return false;
    }

    return true; // Props are equal, skip re-render
  },
);

export default TeamPlayersList;
